import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScheduledUpdatePayload } from "../../api/platformBroadcasts";
import { countdownTargetMs, formatCountdownMs } from "./countdown";

type Props = {
  scheduledUpdate: ScheduledUpdatePayload;
  /** Når true: vis hint om dev-meny (same brukar ser skiftenøkkelen over dette laget). */
  showAdminHint?: boolean;
};

export function UpdatingOverlay({
  scheduledUpdate,
  showAdminHint = false,
}: Props) {
  const { t } = useTranslation();
  const [, setRevision] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setRevision((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const ms = countdownTargetMs(scheduledUpdate.expected_end_at);
  const label = formatCountdownMs(ms);

  return (
    <div
      className="fixed inset-0 z-[400] flex flex-col items-center justify-center bg-slate-900 px-4 text-center text-white"
      role="alertdialog"
      aria-live="assertive"
      aria-label={String(t("platformBroadcast.updatingTitle"))}
    >
      <div className="max-w-lg space-y-4">
        <h1 className="text-xl font-semibold sm:text-3xl">
          {t("platformBroadcast.updatingTitle")}
        </h1>
        <p className="text-sm text-slate-300 sm:text-base">
          {scheduledUpdate.title}
        </p>
        {scheduledUpdate.description ? (
          <p className="text-xs text-slate-400 sm:text-sm">
            {scheduledUpdate.description}
          </p>
        ) : null}
        <div className="rounded-lg bg-slate-800 px-4 py-3 font-mono text-2xl tracking-widest sm:text-4xl">
          {label}
        </div>
        <p className="text-xs text-slate-400 sm:text-sm">
          {t("platformBroadcast.updatingHint")}
        </p>
        {showAdminHint ? (
          <p className="rounded-md border border-slate-600/80 bg-slate-800/80 px-3 py-2 text-[11px] text-slate-300 sm:text-xs">
            {t("platformBroadcast.updatingAdminHint")}
          </p>
        ) : null}
        <span className="sr-only" aria-live="polite">
          {label}
        </span>
      </div>
    </div>
  );
}
